import { useTheme } from "@mui/material";
import { ChartCard } from "@pages:app/analytics/components/cards/ChartCard";
import { DashboardGrid } from "@pages:app/analytics/components/cards/DashboardGrid";
import { DonutCard } from "@pages:app/analytics/components/cards/DonutCard";
import { StatCard } from "@pages:app/analytics/components/cards/StatCard";
import { AnalyticsData } from "@pages:app/analytics/tabs/useAnalyticsData";
import { useTranslation } from "react-i18next";
import {
  MdBarChart,
  MdOutlineDescription,
  MdOutlineSubject,
  MdOutlineTranslate,
} from "react-icons/md";

type TranslationTabProps = {
  data: AnalyticsData;
};

const getFileTypeColor = (fileType: string, defaultColor: string) => {
  const colors = new Map(
    Object.entries({
      Word: "#1764D4",
      PowerPoint: "#BC3719",
    }).map(([key, value]) => [key.toLowerCase(), value]),
  );

  return colors.get(fileType.toLowerCase()) ?? defaultColor;
};

export const TranslationTab = ({ data }: TranslationTabProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const iconColor = theme.palette.primary.main;
  const {
    summaryQuery,
    translationSummaryQuery,
    languagePairsQuery,
    translationFileTypeQuery,
    activeUsersChart,
    requestsChart,
    topUsersSlices,
    topUsersQuery,
    languagePairSlices,
    fileTypeSlices,
    sumSlices,
    formatCompact,
  } = data;

  return (
    <DashboardGrid>
      {/* Row 1 */}
      <StatCard
        label={t("analytics.requests")}
        value={summaryQuery.data?.requestsPeriod}
        icon={<MdBarChart size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.totalTranslations")}
        value={translationSummaryQuery.data?.totalTranslations}
        icon={<MdOutlineTranslate size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.pagesTranslated")}
        value={translationSummaryQuery.data?.totalPages}
        icon={<MdOutlineDescription size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.wordsTranslated")}
        value={translationSummaryQuery.data?.totalWords}
        icon={<MdOutlineSubject size={22} color={iconColor} />}
        colSpan={3}
        format={formatCompact}
      />

      {/* Row 2 */}
      <ChartCard
        title={t("analytics.activeUsers")}
        xAxis={activeUsersChart.xAxis}
        yAxis={activeUsersChart.yAxis}
        series={activeUsersChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />
      <ChartCard
        title={t("analytics.numberOfRequests")}
        xAxis={requestsChart.xAxis}
        yAxis={requestsChart.yAxis}
        series={requestsChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />

      {/* Row 3 */}
      <DonutCard
        title={t("analytics.usersByUsage")}
        slices={topUsersSlices}
        centerValue={sumSlices(topUsersSlices)}
        centerLabel={t("analytics.unit.requests")}
        loading={topUsersQuery.isLoading}
        emptyText={t("analytics.empty.activity")}
        colSpan={4}
      />
      <DonutCard
        title={t("analytics.languagePairs")}
        slices={languagePairSlices}
        centerValue={sumSlices(languagePairSlices)}
        centerLabel={t("analytics.unit.translations")}
        loading={languagePairsQuery.isLoading}
        emptyText={t("analytics.empty.translations")}
        colSpan={4}
      />
      <DonutCard
        title={t("analytics.documentTypes")}
        slices={fileTypeSlices.map((slice) => ({
          ...slice,
          color: getFileTypeColor(slice.id, slice.color),
        }))}
        centerValue={sumSlices(fileTypeSlices)}
        centerLabel={t("analytics.unit.documents")}
        loading={translationFileTypeQuery.isLoading}
        emptyText={t("analytics.empty.documents")}
        colSpan={4}
      />
    </DashboardGrid>
  );
};
